import {
  FieldError,
  Label,
  Text,
  TextArea as AriaTextArea,
  TextField as AriaTextField,
  type TextFieldProps as AriaTextFieldProps,
} from 'react-aria-components';

import type { FieldWidth } from '../TextField/TextField.tsx';
import styles from './TextArea.module.css';

export interface TextAreaProps extends Omit<
  AriaTextFieldProps,
  'children' | 'className' | 'style' | 'type'
> {
  /** Always visible, above the field. Never use the placeholder as the label. */
  label: string;
  description?: string;
  /** What is wrong and how to fix it. Setting it marks the field invalid. */
  errorMessage?: string;
  placeholder?: string;
  /** Visible lines before it scrolls. */
  rows?: number;
  /** Width by expected content; unset fills the form width. */
  width?: FieldWidth;
}

/** Several lines of text: a reason for a correction, an override, a cancellation. */
export function TextArea({
  label,
  description,
  errorMessage,
  placeholder,
  rows = 3,
  width,
  isInvalid,
  ...rest
}: TextAreaProps) {
  return (
    <AriaTextField
      {...rest}
      isInvalid={isInvalid === true || errorMessage !== undefined}
      className={styles.field}
      data-width={width}
    >
      <Label className={styles.label}>{label}</Label>
      <AriaTextArea
        className={styles.input}
        rows={rows}
        {...(placeholder === undefined ? {} : { placeholder })}
      />
      <FieldError className={styles.error}>{errorMessage}</FieldError>
      {description === undefined ? null : (
        <Text slot="description" className={styles.description}>
          {description}
        </Text>
      )}
    </AriaTextField>
  );
}
